/**
 * QL-664 — the mandate's own decision card: "Mandate deviation" on a breach,
 * "Missing mandate" when a required goal has none. One shape for both: a ⚠
 * title with a subtext, a radio list whose options the caller supplies (an
 * update / create action first, then accept with a note, then block), the
 * note under Accept, Save bottom-right of the options (Clear beside it when a
 * decision is stored), and an optional footer under a divider ("Update
 * portfolio from mandate").
 *
 * Deliberately not the shared `DeviationDecisionCard` (the two compliance
 * charts): that card has a fixed Accept/Block pair and no footer, and the
 * mandate's extra options would widen it for two callers that never need
 * them. The option ids are stable strings — part II of the mandate
 * adjustments attaches its persistent "basis portfolio" mode to them.
 *
 * The caller owns the state and the mutations; every string comes in
 * through props, so this file carries no translation table.
 */

import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export type MandateCardOptionId =
  | "update_from_portfolio"
  | "update_from_goal"
  | "create_from_portfolio"
  | "accepted"
  | "do_not_proceed";

export type MandateCardOption = {
  id: MandateCardOptionId;
  label: string;
  hint: string;
  /** Offered but not selectable; `disabledHint` says why (the server's reason). */
  disabled?: boolean;
  disabledHint?: string;
  /** QL-677 — already in effect (this portfolio is the mandate's basis): selectable, Save does nothing. */
  saved?: boolean;
};

type Props = {
  /** Prefix for the control ids, unique per goal or portfolio (`md-${id}`). */
  idPrefix: string;
  title: string;
  subtitle: string;
  options: MandateCardOption[];
  value: MandateCardOptionId | "";
  onValueChange: (value: MandateCardOptionId) => void;
  justification: string;
  onJustificationChange: (value: string) => void;
  noteLabel: string;
  notePlaceholder?: string;
  saveLabel: string;
  clearLabel: string;
  /** A decision is stored server-side, so Clear is offered. */
  hasStoredDecision: boolean;
  onSave: (value: MandateCardOptionId) => void;
  onClear: () => void;
  isSaving?: boolean;
  /** True on a locked (submitted / completed) instance: shown, never edited. */
  disabled?: boolean;
  footer?: ReactNode;
};

export function MandateDeviationCard({
  idPrefix,
  title,
  subtitle,
  options,
  value,
  onValueChange,
  justification,
  onJustificationChange,
  noteLabel,
  notePlaceholder,
  saveLabel,
  clearLabel,
  hasStoredDecision,
  onSave,
  onClear,
  isSaving = false,
  disabled = false,
  footer,
}: Props) {
  const picked = options.find((o) => o.id === value);
  const canSave =
    !!picked &&
    !picked.disabled &&
    !picked.saved &&
    !(value === "accepted" && !justification.trim());

  return (
    <div className="space-y-3 border rounded-lg p-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
          <h4 className="text-base font-medium leading-none">{title}</h4>
        </div>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>

      {/* Options on the left, Save at the bottom-right of the same block. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 flex-1 space-y-3">
          <RadioGroup
            value={value}
            onValueChange={(next) => onValueChange(next as MandateCardOptionId)}
            disabled={disabled}
          >
            {options.map((o) => {
              const id = `${idPrefix}-${o.id}`;
              return (
                <OptionRow key={o.id} align="start">
                  <RadioGroupItem
                    value={o.id}
                    id={id}
                    className="mt-0.5"
                    disabled={disabled || o.disabled}
                  />
                  <Label
                    htmlFor={id}
                    className={cn(
                      "font-normal cursor-pointer",
                      o.disabled && "text-muted-foreground",
                    )}
                  >
                    {o.label}
                    <span className="block text-xs text-muted-foreground">
                      {o.disabled && o.disabledHint ? o.disabledHint : o.hint}
                    </span>
                  </Label>
                </OptionRow>
              );
            })}
          </RadioGroup>

          {value === "accepted" && (
            <div className="space-y-2">
              <Label htmlFor={`${idPrefix}-justification`}>{noteLabel}</Label>
              <Textarea
                id={`${idPrefix}-justification`}
                value={justification}
                onChange={(e) => onJustificationChange(e.target.value)}
                placeholder={notePlaceholder}
                rows={3}
                disabled={disabled}
              />
            </div>
          )}
        </div>

        {!disabled && (
          <div className="flex shrink-0 justify-end gap-2">
            {hasStoredDecision && (
              <Button variant="outline" size="sm" onClick={onClear} loading={isSaving}>
                {clearLabel}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => value && onSave(value)}
              loading={isSaving}
              disabled={!canSave}
            >
              {saveLabel}
            </Button>
          </div>
        )}
      </div>

      {footer && <div className="border-t pt-3">{footer}</div>}
    </div>
  );
}
